// What sits behind the task's overflow menu, and why so little is left in it.
//
// Quick Actions had grown to seven controls stacked full-width down the right
// column, all styled alike, so the two that carry the task's state — In Progress
// and Complete — had no more weight than an escape hatch to another product.
//
// Measured on dev before cutting anything, across a 200-task sample:
//   assigned to somebody   0
//   with a follow-up date  0
// Neither is dead code — both write real fields — but neither has ever been used
// here, so neither earns a permanent button.
//
// Two were removed rather than demoted:
//   Schedule Appointment — the AI Scheduling panel sits in this same dialog and
//     does the job properly. The button predates it.
//   Open in Spruce — went to the Spruce app root, carrying no reference to this
//     task or conversation.
//
// In Progress and Complete stay as buttons. Everything else is here.
export type TaskActionKey = 'assign' | 'snooze' | 'profile' | 'delete';

export interface TaskAction {
  key: TaskActionKey;
  label: string;
  hint: string;
  destructive?: boolean;
  disabled?: boolean;
}

export interface TaskActionContext {
  // Delete is refused by the backend for any task carrying a patient message,
  // so it is only offered where it can actually succeed.
  isRecallTask?: boolean;
  deleting?: boolean;
}

export function overflowActions(context: TaskActionContext = {}): TaskAction[] {
  const actions: TaskAction[] = [
    {
      key: 'assign',
      label: 'Assign to…',
      hint: 'Puts somebody’s name on this task. It stays on the board either way — this does not move or hide it.',
    },
    {
      key: 'snooze',
      // It was called "Follow Up", which reads like adding a reminder. It does
      // the opposite of adding something: routes/triage.ts filters the board to
      // tasks whose followUpDate has passed, so setting one takes the task off
      // the board until that day. Somebody would have found that out the hard way.
      label: 'Snooze until…',
      hint: 'Hides this task from the board until the date you pick, then brings it back. Nothing is sent to the patient.',
    },
    {
      // The chart drawer is the quick peek; the profile is the full record —
      // hero, Details, AI, Messages, Documents, Billing, History — and opens in
      // the same tab with a Back to task button. From a menu, where somebody has
      // deliberately gone looking, the full record is what they want.
      key: 'profile',
      label: 'Open patient profile',
      hint: 'Opens this patient’s full record — details, messages, documents, billing and history — with a Back to task button to return here.',
    },
  ];

  if (context.isRecallTask) {
    actions.push({
      key: 'delete',
      label: 'Delete this task',
      hint: 'Removes this recall task and puts the patient back on the wellness list. Only recall tasks can be deleted.',
      destructive: true,
      disabled: !!context.deleting,
    });
  }

  return actions;
}
